import { ensureFreshSession, getSessionKey, isAuthenticatedSession } from "../helpers/auth_session";
import { __vueEvent } from "../constant";
import { store } from "../store/store";
import { SIGNALR_STATUS } from "../enums/signalr_status";
import { onExceptionOccured, logServerSideDebug } from "../helpers/on_exception_occured";
import * as signalR from "@microsoft/signalr";
import {
  getAppUrl,
  onMessageFromServer,
  getMessageFromServer,
  removeDataFromRedis,
  getMsgFromContent,
  onTypingFromServer,
  onConfigMessageFromServer,
  onProcessingFromServer
} from "../helpers/index";
import { STORE_MUTATION, EVENTS, CACHE_ID_TYPE } from "../enums";
import { MessageStructure, MessageStructureComponent } from "../types";
import { setCookie } from "../helpers/cookie_handler";

let signalrConnection: signalR.HubConnection;
let isConnectToSignalRCalled = false;
export class SignalrHandler {
  reconnectTimeValue = store.state.retryInterval;

  disconnectCount = 0;
  stop() {
    signalrConnection.stop();
    console.log("signalr disconnected at: ", new Date());
  }

  constructor() {
    this.listenEvents();
    signalrConnection = new signalR.HubConnectionBuilder()
      .withUrl(`${getAppUrl()}chatHub`, {
        skipNegotiation: true,
        transport: signalR.HttpTransportType.WebSockets
      })
      .configureLogging(signalR.LogLevel.None)
      .build();
  }

  public listenEvents() {
    __vueEvent
      .on(EVENTS.ConnectSignalr, () => {
        if (
          signalrConnection.state === signalR.HubConnectionState.Disconnected && store.state.focusedWindow
        ) {
          this.disconnectCount = 0;
          this.retryConnection();
        }
      });
      __vueEvent.on(EVENTS.StopSignalr, this.stop);
      __vueEvent.on(EVENTS.RemoveDataFromRedis, () => {
        this.removeDataFromRedis();
      });
      // SD-13570: the host signed this visitor out. Their conversation stays
      // on the server and comes back if they sign in again, but it must not
      // stay on the screen — the next person at a shared browser would
      // otherwise read it straight off the page, whatever the server refuses.
      __vueEvent.on(EVENTS.AuthSessionCleared, () => {
        store.commit(STORE_MUTATION.SetMessages, []);
      });
      // SD-13570: the server moved us to a different conversation — a second
      // verified user on a shared browser. The socket is still in the previous
      // conversation's group, where nothing addressed to us will ever arrive,
      // so re-join under the new id and pull what belongs to it.
      __vueEvent.on(EVENTS.ConversationReassigned, async () => {
        if (signalrConnection?.state === signalR.HubConnectionState.Connected) {
          await this.sendClientInfo();
        }
        await this.fetchFailedMessages();
      });
      // SD-13570: the token can arrive after we have already joined and asked
      // for history. Re-join so the hub admits us to the stamped conversation,
      // then pull the transcript that was refused while we were anonymous.
      __vueEvent.on(EVENTS.AuthSessionEstablished, async () => {
        if (signalrConnection?.state === signalR.HubConnectionState.Connected) {
          await this.sendClientInfo();
        }
        await this.fetchFailedMessages();
      });
  }

 async removeDataFromRedis() {
  const msgs = await removeDataFromRedis();
 }

  /**
   * Fetches failed messages from the server and updates the state with the retrieved messages.
   * @returns {Promise<void>} A promise that resolves when the messages are fetched and updated.
   */
  async fetchFailedMessages() {
    let lastMessageId = 0;
    const stateMessageLength = store.state.messages.length;
    for (let i = stateMessageLength - 1; i >= 0; i--) {
      if (Number(store.state.messages[i].id)) {
        lastMessageId = Number(store.state.messages[i].id);
        break;
      }
    }
    // Enable auto-scroll suppression before processing recovery
    store.commit(STORE_MUTATION.SetSuppressNextScroll, true);

    // Get messages from the server
    const msgs = await getMessageFromServer(
      lastMessageId,
      CACHE_ID_TYPE.AfterMessageCacheId,
      true,
      store.state.clientVersion,
      `signalr_handler.ts:fetchFailedMessages`
    );

    const msgLength = msgs.length;
    var stateMsgLength = store.state.messages.length - 1;
    if (msgLength > 0) {
      try {
        localStorage.setItem(msgs[0].keys.extConvId, msgs[0].messageTime);
      }
      catch(ex){
        setCookie(msgs[0].keys.extConvId, msgs[0].messageTime);
      }
      // ctx.commit(STORE_MUTATION.SetIsAppInAdaMode, true);
      msgs.forEach(function (msg, index) {
        if (
          store.state.messages.every(m => m.id != msg.recordInfo.messageCacheId)
        ) {

          if (store.state.isAnchorTagClickedSametabNewtab &&
            store.state.messages[stateMsgLength].contents.keys["ExtMsgId"] === msg.keys["ExtMsgId"]) {

            store.commit(STORE_MUTATION.SetRequestResponseReceived, true);
            store.state.messages[stateMsgLength].id = msgs[0].recordInfo.messageCacheId;
            store.commit(STORE_MUTATION.SetIsAnchorTagClickedSametabNewtab, false);
            return;
          }

          // SD-13404-followup: mirror the inputHidden guard already applied in
          // actions.ts's initial-load hydration - without it, a message sent via
          // ?input=...&inputHidden=1 resurfaces here on every SignalR reconnect.
          if (!(msg.settings && msg.settings.inputHidden)) {
            store.commit(STORE_MUTATION.AddMessage, getMsgFromContent(msg));
          }
        }

        if (index !== msgLength - 1) {
          store.commit(STORE_MUTATION.RemoveAllButtons);
        }
      });
    }
  }

  lastRetryExecution = 0; // Store the last time retryConnection was called
  retryDelay = 500;		  // Prevent any duplicate calls within 500ms

  retryConnection = () => {

    store.commit(STORE_MUTATION.SetIsSignalRConnected, false);

	// WEBAPPS-959: This logic is used to prevent function from executing multiple times within 500ms delay
	if (this.lastRetryExecution >= (Date.now() - this.retryDelay))
    	return;
	this.lastRetryExecution = Date.now();

    setTimeout(async () => {
      __vueEvent.emit(SIGNALR_STATUS.Connecting);
      try {
        await this.createConnection_();
        this.fetchFailedMessages();
      } catch (ex) {
        isConnectToSignalRCalled = false;
        ++this.disconnectCount;
        if (this.disconnectCount < store.state.retryWarningCount && store.state.focusedWindow) {
          this.retryConnection();
        } else {
          __vueEvent.emit(SIGNALR_STATUS.Disconnected);
        }
      }
    }, this.reconnectTimeValue);
  };

  listenToConnectionEvents_() {
    signalrConnection.onclose((error) => {
      // Added detailed logging for connection closure with error information
      /*if (error) {
        console.log(
          JSON.stringify({
            level: "error",
            message: `[signalr_handler] SignalR connection closed with error: ${error?.message ?? "N/A"}`,
            method: "signalrConnection.onclose",
            state: signalrConnection?.state ?? "unknown",
            timestamp: new Date().toISOString(),
          }),
        );
      } else {
        console.log("[signalr_handler] Connection closed cleanly (manually stopped).");
      }*/
      
		// If the current tab is in focus, then only reconnect
		if (!!store.state.focusedWindow) {
			this.retryConnection();
		}
    });
  }

  async createConnection_() {
    if (
      signalrConnection.state === signalR.HubConnectionState.Disconnected &&
      isConnectToSignalRCalled == false
    ) {
      if (typeof WebSocket.OPEN == "function") {
        Object.defineProperty(WebSocket, "OPEN", { value: 1 });
      }
      isConnectToSignalRCalled = true;
      await signalrConnection.start();
      isConnectToSignalRCalled = false;
      await this.sendClientInfo();
      // The mirror of the AuthSessionEstablished listener: if the token landed
      // before this handler existed, that event has already been and gone, and
      // the load-time history fetch was refused while we were anonymous.
      if (isAuthenticatedSession()) {
        await this.fetchFailedMessages();
      }
      __vueEvent.emit(SIGNALR_STATUS.Connected);
      store.commit(STORE_MUTATION.SetIsSignalRConnected, true);
    }
  }

  async sendClientInfo() {
    // SD-13570 D6b — reconnect is one of the moments we re-confirm. The socket
    // may have been down long enough for the visitor to have signed out of the
    // client site, and this join is what re-establishes their access to a
    // stamped conversation.
    await ensureFreshSession();

    var conInfo = {
      ConversationId: store.state.convId,
      BotId: store.state.extBotId ? store.state.extBotId.toString() : "",
      // SD-13570 Step 7: the hub checks conversation ownership, and a socket
      // join carries no cookies it can rely on — so the session key travels
      // with the join itself. Empty for anonymous joins, which stay allowed.
      SessionKey: getSessionKey()
    };

    signalrConnection.send("SetClientInfo", conInfo);
  }

  async start() {
    __vueEvent.emit(SIGNALR_STATUS.Connecting);
    this.listentToServerEvents_();
    this.listenToConnectionEvents_();
    try {
      await this.createConnection_();
    } catch (ex) {
      isConnectToSignalRCalled = false;
      //onExceptionOccured("start in signalr handler", ex);
    }
    window["signalrConnection"] = signalrConnection;
  }

  listentToServerEvents_() {
    signalrConnection.on("message", onMessageFromServer);
    signalrConnection.on("typing", onTypingFromServer);
    signalrConnection.on("processing", onProcessingFromServer);
    signalrConnection.on("page_config", onConfigMessageFromServer);
  }
}
